import { RecipeResponse } from 'lib/services/models/content-response';
import { RecipeHeaderProps } from './index';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { getEnumValues } from 'lib/utils/sitecore/enum';
import { Data } from '@generated/Foundation.GMI.model';

/**
 * Extracts header data from a RecipeResponse object
 * @param recipe - The full recipe response object
 * @param siteSettings - Optional site settings for recipe header settings
 * @returns RecipeHeaderProps object with only the properties needed for the recipe header
 */
export function extractRecipeHeaderData(
  recipe: RecipeResponse,
  siteSettings?:
    | (SiteSettings & {
        recipeDetailSiteSettings?: Data.Settings.RecipeDetailSiteSettings;
      })
    | null,
  videoTranscriptUrls?: Record<string, string | undefined>,
  videoWithAudioDescriptionURL?: string
): RecipeHeaderProps {
  // Calculate total number of ingredients
  const totalIngredients = recipe.IngredientGroups?.reduce(
    (total, group) => total + (group?.Ingredients?.length || 0),
    0
  );

  const { recipeDetailSiteSettings } = siteSettings || {};

  const rdpHeaderSettings: RecipeHeaderProps['rdpHeaderSettings'] = {
    hideRdpHeader: recipeDetailSiteSettings?.fields?.hideRdpHeader?.value,
    hideRdpHeaderRatingsAndReviews:
      recipeDetailSiteSettings?.fields?.hideRdpHeaderRatingsAndReviews?.value,
    hideRdpHeaderHeadline: recipeDetailSiteSettings?.fields?.hideRdpHeaderHeadline?.value,
    hideRdpHeaderVideo: recipeDetailSiteSettings?.fields?.hideRdpHeaderVideo?.value,
    hideRdpHeaderAttributes: recipeDetailSiteSettings?.fields?.hideRdpHeaderAttributes?.value,
    hideRdpHeaderAttributeList: getEnumValues(
      recipeDetailSiteSettings?.fields?.hideRdpHeaderAttributeList
    ),
  };

  const { VideoId, Title, ThumbnailUrl } = recipe.AssociatedVideos?.[0] ?? {};

  return {
    // Image
    imageUrl: recipe.StandardImageUrl,
    imageAlt: '', // Descriptive alt text is not available from the GMI content service

    // Video
    videoId: VideoId || '',
    videoTitle: Title || '',
    videoUrl: `https://www.youtube.com/watch?v=${VideoId || ''}`,
    videoThumbnailUrl: ThumbnailUrl || '',

    videoWithAudioDescriptionURL: videoWithAudioDescriptionURL || '',

    videoTranscriptFileUrl: videoTranscriptUrls?.[VideoId || ''] || '',

    // Basic info
    title: recipe.Title,

    // Attributes info
    prepTime: recipe.PrepTime ?? '',
    totalTime: recipe.TotalTime ?? '',
    servingNumber: recipe.ServingNumber ?? '',
    caloriesPerServing:
      recipe.NutritionItems?.find((item) => item.TranslatedName === 'Calories')?.DisplayValue ?? '',
    totalIngredients: totalIngredients || 0,

    // Rating info
    contentId: recipe.ContentId,
    ratingAndReviewContentId: recipe.RatingAndReviewContentId,

    // RDP Header Settings
    rdpHeaderSettings,
  };
}
